import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/fetcher';
import { ApiEndpoint } from '@/api/registry';
import type {
  HistoricalPerformanceMetric,
  HistoricalPerformanceResponse,
} from '@/features/decisions/decisions.api.types';
import { useTenant } from '@/providers/TenantProvider';

export interface UseHistoricalPerformanceParams {
  scenarioRunId: string;
  runId: string;
  sku: string;
  site: string;
  refExperimentId: string;
  experimentIds: string[];
  periodFrom: string;
  periodTo: string;
}

// Owns the SKU x Site detail data: header (active recommendation, FCF opportunity)
// plus the per-experiment metric series rendered by the detail page.
export function useHistoricalPerformance({
  scenarioRunId,
  runId,
  sku,
  site,
  refExperimentId,
  experimentIds,
  periodFrom,
  periodTo,
}: UseHistoricalPerformanceParams) {
  const { tenantAlias } = useTenant();

  const query = useQuery<HistoricalPerformanceResponse, Error>({
    queryKey: [
      'hist-perf',
      tenantAlias,
      scenarioRunId,
      runId,
      sku,
      site,
      refExperimentId,
      experimentIds,
      periodFrom,
      periodTo,
    ],
    enabled: Boolean(
      scenarioRunId && runId && sku && site && refExperimentId && experimentIds.length > 0 && periodFrom && periodTo,
    ),
    retry: false,
    staleTime: 30_000,
    queryFn: () =>
      apiFetch<HistoricalPerformanceResponse>(tenantAlias, ApiEndpoint.SimulationHistoricalPerformance, {
        pathParams: { scenarioRunId, runId, sku, site },
        query: {
          ref_experiment_id: refExperimentId,
          experiment_id: experimentIds,
          period_from: periodFrom,
          period_to: periodTo,
        },
      }),
  });

  const metrics = useMemo<HistoricalPerformanceMetric[]>(() => query.data?.metrics ?? [], [query.data]);

  return {
    header: query.data?.header,
    metrics,
    resolved: query.data?.resolved,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}
